import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';

export function HomePage() {
  return (
    <div className="overflow-hidden">
      {/* Section 1: Hero */}
      <section className="section-padding bg-slate-50 dark:bg-obsidian">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-obsidian dark:text-signal leading-tight">
              We pinpoint why SaaS companies lose clients.{' '}
              <span className="text-amber">Then we build the system to stop it.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl mx-auto">
              Retention consulting and an AI-powered system builder for Series A+ operators who treat churn as an engineering problem.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://app.churnlogix.com">
                <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                  Try the retention builder
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" size="lg">
                  Book a diagnostic
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Three tiers */}
      <section className="section-padding bg-white dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
              Three ways to work with ChurnLogix
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1: Newsletter */}
            <Card variant="bordered" padding="lg">
              <CardContent>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Free
                </span>
                <h3 className="mt-3 text-xl font-heading font-black text-obsidian dark:text-signal">
                  The framework, weekly.
                </h3>
                <p className="mt-4 text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  Every issue covers one piece of the retention system. Signals, risk categories, intervention playbooks, or renewal architecture. Written for operators, not entry-level CS teams.
                </p>
                <div className="mt-6">
                  <a
                    href="https://churnlogix.substack.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm" className="w-full">
                      Subscribe
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Retention Builder - Featured */}
            <Card variant="bordered" padding="lg" className="border-amber">
              <CardContent>
                <span className="text-xs font-semibold text-amber uppercase tracking-wider">
                  From $49/mo
                </span>
                <h3 className="mt-3 text-xl font-heading font-black text-obsidian dark:text-signal">
                  Build your system yourself.
                </h3>
                <p className="mt-4 text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  The AI walks you through all five modules. Signal architecture, risk categories, intervention playbooks, leading metrics, and renewal process. Answer its questions. It builds your retention system documentation.
                </p>
                <div className="mt-6">
                  <a href="https://app.churnlogix.com">
                    <Button variant="primary" size="sm" className="w-full">
                      Start building
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Diagnostic Engagement */}
            <Card variant="bordered" padding="lg">
              <CardContent>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Custom
                </span>
                <h3 className="mt-3 text-xl font-heading font-black text-obsidian dark:text-signal">
                  We build it with you.
                </h3>
                <p className="mt-4 text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  For operators who want the system built properly from the ground up. We diagnose your specific churn patterns, deliver recommendations tailored to your business, then implement the architecture.
                </p>
                <div className="mt-6">
                  <Link to="/contact">
                    <Button variant="outline" size="sm" className="w-full">
                      Book a diagnostic
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 3: Proof strip */}
      <section className="py-12 bg-slate-100 dark:bg-[#0F1014]">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
                2,000+
              </p>
              <p className="mt-2 text-sm text-slate-500">
                contracts analysed
              </p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
                4
              </p>
              <p className="mt-2 text-sm text-slate-500">
                retention systems built
              </p>
            </div>
            <div>
              <p className="text-lg md:text-xl font-heading font-black text-obsidian dark:text-signal leading-tight">
                Churn identified before renewal
              </p>
              <p className="mt-2 text-sm text-slate-500">
                not after
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: The problem */}
      <section className="section-padding bg-white dark:bg-obsidian">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
              Your team finds out clients are leaving when they cancel.
            </h2>
            <div className="mt-8 space-y-6 text-slate-600 dark:text-slate-400 leading-relaxed">
              <p>
                Most SaaS companies operate retention reactively. The customer success team discovers a client is unhappy when the renewal conversation starts going sideways. By then, the damage is done. The warning signs were there for months. Usage dropped. Key contacts stopped responding. Support tickets went from questions to complaints. But nobody connected the dots until it was too late to act.
              </p>
              <p>
                A properly engineered retention system identifies risk before it becomes churn. It categorizes accounts by behavior patterns, triggers interventions at the right moments, and gives your team clear priorities instead of scattered intuition. The difference is not working harder. The difference is knowing which accounts need attention now, which need attention next quarter, and which are fine.
              </p>
            </div>
            <div className="mt-10">
              <Link to="/how-it-works">
                <Button variant="outline" rightIcon={<ArrowRight className="w-5 h-5" />}>
                  How we build it
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Tool preview */}
      <section className="section-padding bg-slate-50 dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Description */}
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
                The Retention System Builder
              </h2>
              <div className="mt-8 space-y-4 text-slate-600 dark:text-slate-400 leading-relaxed">
                <p>
                  Five modules that structure your entire retention operation. Signal architecture defines what engagement looks like. Risk categories group accounts by behavior. Intervention playbooks give your team specific actions. Leading metrics track what matters. Renewal process systematizes the end-to-end workflow.
                </p>
                <p>
                  The AI guides you through each module with questions specific to your business. As you answer, it extracts structured information and builds your retention system document in real time.
                </p>
                <p>
                  When you finish, you have a complete, documented retention system you can share with your team and implement immediately.
                </p>
              </div>
              <div className="mt-8">
                <a href="https://app.churnlogix.com">
                  <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                    Try it free
                  </Button>
                </a>
              </div>
            </div>

            {/* Right: UI Mockup */}
            <div className="bg-white dark:bg-obsidian-50 border border-slate-200 dark:border-slate-700 rounded p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-4 text-xs text-slate-500">Retention System Builder</span>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Sidebar */}
                <div className="col-span-1 space-y-2">
                  <div className="p-2 bg-amber/10 border border-amber/30 rounded text-xs text-amber">
                    1. Signal Architecture
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-obsidian border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-500 dark:text-slate-400">
                    2. Risk Categories
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-obsidian border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-500 dark:text-slate-400">
                    3. Intervention Playbooks
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-obsidian border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-500 dark:text-slate-400">
                    4. Leading Metrics
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-obsidian border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-500 dark:text-slate-400">
                    5. Renewal Process
                  </div>
                </div>

                {/* Chat area */}
                <div className="col-span-2 space-y-3">
                  <div className="p-3 bg-slate-50 dark:bg-obsidian border border-slate-200 dark:border-slate-700 rounded">
                    <p className="text-xs text-slate-700 dark:text-slate-300">
                      What is the primary action that indicates a user is getting value from your product?
                    </p>
                  </div>
                  <div className="p-3 bg-amber/10 border border-amber/20 rounded ml-4">
                    <p className="text-xs text-slate-700 dark:text-slate-300">
                      When they complete their first report and share it with their team.
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-obsidian border border-slate-200 dark:border-slate-700 rounded">
                    <p className="text-xs text-slate-700 dark:text-slate-300">
                      Good. I will use "Report completion + team share" as your primary engagement signal. What secondary signals indicate ongoing engagement?
                    </p>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white dark:bg-obsidian border border-slate-300 dark:border-slate-600 rounded">
                    <div className="flex-1 text-xs text-slate-400">Type your response...</div>
                    <div className="w-6 h-6 bg-amber rounded flex items-center justify-center">
                      <ArrowRight className="w-3 h-3 text-obsidian" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Case study teaser */}
      <section className="py-16 bg-slate-100 dark:bg-[#0F1014]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-2xl md:text-3xl font-heading font-black text-obsidian dark:text-signal leading-tight">
              "Identified and saved 23 at-risk accounts in Q1 representing $2.3M in ARR"
            </p>
            <p className="mt-4 text-sm text-slate-500">
              B2B SaaS, Health Tech, 2000+ contracts
            </p>
            <div className="mt-8">
              <Link to="/projects">
                <Button variant="outline" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                  Read the full case study
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: Final CTA */}
      <section className="section-padding bg-white dark:bg-obsidian border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
            Ready to stop finding out too late?
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://app.churnlogix.com">
              <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                Try the retention builder
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" size="lg">
                Book a diagnostic
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
